'use client';

import {
  useId,
  useRef,
  type ComponentPropsWithRef,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Icon, type IconName } from './icon';
import styles from './selection-controls.module.css';

const cx = (...names: Array<string | false | null | undefined>) =>
  names.filter(Boolean).join(' ');

type ChoiceProps = Omit<ComponentPropsWithRef<'input'>, 'type'> & {
  label: ReactNode;
  description?: ReactNode;
};

export function Radio({ label, description, disabled, className, ...rest }: ChoiceProps) {
  const id = useId();
  return (
    <label className={cx(styles.choice, disabled && styles.isDisabled, className)} htmlFor={id}>
      <input type="radio" id={id} disabled={disabled} className={styles.input} {...rest} />
      <span className={cx(styles.box, styles.boxRadio)} aria-hidden="true">
        <span className={styles.dot} />
      </span>
      <span className={styles.text}>
        <span className={styles.label}>{label}</span>
        {description ? <span className={styles.desc}>{description}</span> : null}
      </span>
    </label>
  );
}

export function Checkbox({
  label,
  description,
  disabled,
  size = 'md',
  className,
  ...rest
}: Omit<ChoiceProps, 'size'> & {
  /**
   * `lg` enlarges the box to --ctl-check-lg. Used where checking is the
   * primary action rather than one field among many — the shopping list, which
   * is tapped one-handed while walking a shop.
   *
   * `size` is omitted from the inherited input props deliberately: the native
   * attribute is a numeric character-width that means nothing to a checkbox,
   * and leaving it in intersects to `never`.
   */
  size?: 'md' | 'lg';
}) {
  const id = useId();
  return (
    <label className={cx(styles.choice, disabled && styles.isDisabled, className)} htmlFor={id}>
      <input type="checkbox" id={id} disabled={disabled} className={styles.input} {...rest} />
      <span
        className={cx(styles.box, styles.boxCheck, size === 'lg' && styles.boxLg)}
        aria-hidden="true"
      >
        <Icon name="check" size={size === 'lg' ? 18 : 14} className={styles.tick} />
      </span>
      <span className={styles.text}>
        <span className={styles.label}>{label}</span>
        {description ? <span className={styles.desc}>{description}</span> : null}
      </span>
    </label>
  );
}

type ChoiceGroupProps = Omit<ComponentPropsWithRef<'fieldset'>, 'children'> & {
  legend?: ReactNode;
  helper?: ReactNode;
  error?: ReactNode;
  row?: boolean;
  children: ReactNode;
};

export function ChoiceGroup({
  legend,
  helper,
  error,
  row,
  className,
  children,
  ...rest
}: ChoiceGroupProps) {
  return (
    <fieldset className={cx(styles.fieldset, className)} {...rest}>
      {legend ? <legend className={styles.legend}>{legend}</legend> : null}
      <div className={cx(styles.list, row && styles.listRow)}>{children}</div>
      {error ? (
        <p role="alert" className={cx(styles.msg, styles.msgError)}>
          {error}
        </p>
      ) : helper ? (
        <p className={styles.msg}>{helper}</p>
      ) : null}
    </fieldset>
  );
}

type OptionChipProps = Omit<ComponentPropsWithRef<'button'>, 'children'> & {
  selected?: boolean;
  leadingIcon?: IconName;
  /**
   * `multi` (default) — an independent toggle: `role="checkbox"`, with a +/✓
   * affordance because each chip can be turned on or off on its own.
   * `single` — one choice out of a set: `role="radio"`, and NO +/✓ mark (a
   * radio is expressed by which chip is filled, and a "+" wrongly implies you
   * can add several). Wrap `single` chips in a `<ChipGroup>` so they announce
   * as one radiogroup.
   */
  select?: 'multi' | 'single';
  children: ReactNode;
};

export function OptionChip({
  selected = false,
  leadingIcon,
  select = 'multi',
  className,
  children,
  ...rest
}: OptionChipProps) {
  const single = select === 'single';
  return (
    <button
      type="button"
      role={single ? 'radio' : 'checkbox'}
      aria-checked={selected}
      // Roving tabindex: a radiogroup is ONE tab stop, and Tab lands on the
      // current choice. Multi-select chips are independent, so each keeps its
      // own stop. `rest` spreads after, so a caller can still override.
      tabIndex={single ? (selected ? 0 : -1) : undefined}
      className={cx(styles.chip, selected && styles.isSelected, className)}
      {...rest}
    >
      {leadingIcon ? <Icon name={leadingIcon} size={16} /> : null}
      <span>{children}</span>
      {single ? null : (
        <span className={styles.chipMark} aria-hidden="true">
          <Icon name={selected ? 'check' : 'plus'} size={14} />
        </span>
      )}
    </button>
  );
}

/**
 * Radiogroup wrapper for `single`-select OptionChips — without it a screen
 * reader hears three loose radios with no shared name or label.
 *
 * Also owns the keyboard contract a radiogroup is required to have: arrows move
 * between options and select as they go, Home/End jump to the ends. The chips
 * themselves stay dumb buttons — focus management belongs to the group, which
 * is the only thing that knows what the siblings are.
 */
export function ChipGroup({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step =
      e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1
      : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1
      : 0;
    const isEdge = e.key === 'Home' || e.key === 'End';
    if (!step && !isEdge) return;

    const chips = Array.from(
      ref.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]:not(:disabled)') ?? [],
    );
    if (chips.length === 0) return;

    const current = chips.indexOf(document.activeElement as HTMLButtonElement);
    const next = isEdge
      ? (e.key === 'Home' ? 0 : chips.length - 1)
      // Wrap, and start from the checked chip when focus is still on the group.
      : (((current >= 0 ? current : chips.findIndex((c) => c.getAttribute('aria-checked') === 'true')) +
          step + chips.length) % chips.length);

    e.preventDefault();
    chips[next].focus();
    // A radiogroup selects on arrow, not on a second keypress.
    chips[next].click();
  };

  return (
    <div
      ref={ref}
      role="radiogroup"
      aria-label={label}
      className={cx(styles.chipGroup, className)}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  );
}
